import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, Typography, Chip } from '@mui/material';
import parse from 'html-react-parser';
import { translate as t } from 'hooks/useTranslations';
import { DOMNode } from 'html-react-parser';
import contentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import manualHelper from 'views/App/Portal/Policy/DraftManuals/helpers/draftManualHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import AttachmentBox from 'views/App/Portal/Policy/DraftManuals/Detail/AttachmentLibrary/_components/AttachmentBox';
import { AccreditationTags, PolicyAccreditationTags } from './AccreditationTags';
import type { DraftManual, DraftManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import type { ExportType } from '../types';

export const isFullManual = (manual: any): manual is DraftManual => {
  return manual && 'chapters' in manual && 'prefaces' in manual;
};

interface PreviewContentProps {
  exportType: ExportType;
  manual: DraftManualItem | DraftManual | null | undefined;
  showEditLevelTags: boolean;
  showAccreditationTags: boolean;
  policyAccreditationTags: Record<string, string[]>;
}

const getFormattedTitle = (
  section: any,
  policyNumber: string,
  policyData: any,
  showAccreditationTags: boolean,
  policyAccreditationTags: Record<string, string[]>
) => {
  const baseTitle = manualHelper.formatPolicyNodeTitle(section, policyNumber, policyData);

  if (!showAccreditationTags) {
    return baseTitle;
  }

  const accTags = policyAccreditationTags[section.id] || [];

  if (accTags.length === 0) {
    return baseTitle;
  }

  return <>{baseTitle}</>;
};

export const PreviewContent: React.FC<PreviewContentProps> = ({
  exportType,
  manual,
  showEditLevelTags,
  showAccreditationTags,
  policyAccreditationTags,
}) => {
  const { node } = useParams();

  if (exportType === 'currentContent' && node) {
    const [type, id] = node.split(':');

    if (type === 'policy') {
      const { data: policyData } = useDraftPolicy(manual?.id, id, true, {}, undefined);

      if (!policyData) {
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant='body2'>{t('draftManuals.export.loadingPolicyContent')}</Typography>
          </Box>
        );
      }

      const policyNumber = manual && isFullManual(manual) ? manualHelper.getNumberManualNode(policyData, manual) : '';

      return (
        <Box>
          <Stack
            direction='row'
            spacing={3}
            sx={{ alignItems: 'center', mb: 1 }}
          >
            <Typography variant='caption'>
              {t('draftManuals.export.policyLabel')} {policyNumber}
            </Typography>
            {showAccreditationTags && (
              <PolicyAccreditationTags
                policyData={policyData}
                policyAccreditationTags={policyAccreditationTags}
              />
            )}
          </Stack>

          <Typography
            variant='h6'
            sx={{ mb: 2 }}
          >
            {parse(policyData.title || '')}
          </Typography>

          <Stack spacing={10}>
            {policyData.sections
              ?.filter((section: any) => section.state !== 'DISABLED')
              .map((section: any, index: number) => (
                <Box key={section.id || index}>
                  <Stack spacing={1}>
                    <Typography
                      className={`title_${section.id}`}
                      component='div'
                      variant='h6'
                    >
                      {getFormattedTitle(
                        section,
                        policyNumber,
                        policyData,
                        showAccreditationTags,
                        policyAccreditationTags
                      )}
                    </Typography>
                    {(showEditLevelTags || showAccreditationTags) && (
                      <Stack
                        direction='row'
                        spacing={3}
                      >
                        {showEditLevelTags && (
                          <Chip
                            label={section.editLevelTag || 'AGENCY_CONTENT'}
                            variant='outlined'
                            size='small'
                          />
                        )}
                        {section.agencyModified && showEditLevelTags && (
                          <Chip
                            label={t('draftManuals.agencyModified')}
                            variant='outlined'
                            size='small'
                          />
                        )}
                        {showAccreditationTags && (
                          <AccreditationTags
                            section={section}
                            policyData={policyData}
                            manual={manual}
                            policyAccreditationTags={policyAccreditationTags}
                          />
                        )}
                      </Stack>
                    )}

                    <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                      {parse(contentConversionHelper.contentForRender(section.content ?? ''), {
                        replace(node: DOMNode, ind: number) {
                          return PolicyContentHelper.handleContent(node, ind);
                        },
                      })}
                    </Box>

                    {section.attachments && section.attachments.length > 0 && (
                      <Stack
                        direction='row'
                        spacing={1}
                        sx={{ mt: 2, mb: 2 }}
                      >
                        {section.attachments
                          .filter((att: any) => !att.embedded)
                          .map((att: any) => (
                            <AttachmentBox
                              key={att.id}
                              attachmentRef={att}
                              manualId={manual?.id || ''}
                              onRemove={() => {}}
                              isEditable={false}
                            />
                          ))}
                      </Stack>
                    )}
                  </Stack>
                </Box>
              ))}

            <Stack
              spacing={10}
              sx={{ mt: 10 }}
            >
              {policyData.sections
                ?.filter((section: any) => section.state !== 'DISABLED')
                .map((section: any) =>
                  section.subSections
                    ?.filter((subSection: any) => subSection.state !== 'DISABLED')
                    .map((subSection: any, subSectionIndex: number) => (
                      <Box key={`${subSection.id}_${subSectionIndex}`}>
                        <Stack spacing={1}>
                          <Typography
                            className={`title_${subSection.id}`}
                            component='div'
                            variant='h6'
                          >
                            {getFormattedTitle(
                              subSection,
                              policyNumber,
                              policyData,
                              showAccreditationTags,
                              policyAccreditationTags
                            )}
                          </Typography>
                          {(showEditLevelTags || showAccreditationTags) && (
                            <Stack
                              direction='row'
                              spacing={3}
                            >
                              {showEditLevelTags && (
                                <Chip
                                  label={subSection.editLevelTag || 'AGENCY_CONTENT'}
                                  variant='outlined'
                                  size='small'
                                />
                              )}
                              {subSection.agencyModified && showEditLevelTags && (
                                <Chip
                                  label={t('draftManuals.agencyModified')}
                                  variant='outlined'
                                  size='small'
                                />
                              )}
                              {showAccreditationTags && (
                                <AccreditationTags
                                  section={subSection}
                                  policyData={policyData}
                                  manual={manual}
                                  policyAccreditationTags={policyAccreditationTags}
                                />
                              )}
                            </Stack>
                          )}

                          <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                            {parse(contentConversionHelper.contentForRender(subSection.content ?? ''), {
                              replace(node: DOMNode, ind: number) {
                                return PolicyContentHelper.handleContent(node, ind);
                              },
                            })}
                          </Box>

                          {subSection.attachments && subSection.attachments.length > 0 && (
                            <Stack
                              direction='row'
                              spacing={1}
                              sx={{ mt: 2, mb: 2 }}
                            >
                              {subSection.attachments
                                .filter((att: any) => !att.embedded)
                                .map((att: any) => (
                                  <AttachmentBox
                                    key={att.id}
                                    attachmentRef={att}
                                    manualId={manual?.id || ''}
                                    onRemove={() => {}}
                                    isEditable={false}
                                  />
                                ))}
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    ))
                )}
            </Stack>
          </Stack>
        </Box>
      );
    }

    if (type === 'preface') {
      const { data: prefaceData } = useDraftPreface(manual?.id, id, true, {}, undefined);

      if (!prefaceData) {
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant='body2'>{t('draftManuals.export.loadingPrefaceContent')}</Typography>
          </Box>
        );
      }

      return (
        <Box>
          <Typography
            variant='h6'
            sx={{ mb: 2 }}
          >
            {parse(prefaceData.title || '')}
          </Typography>

          <Typography variant='body2'>
            {parse(contentConversionHelper.contentForRender(prefaceData.content ?? ''), {
              replace(node: DOMNode, ind: number) {
                return PolicyContentHelper.handleContent(node, ind);
              },
            })}
          </Typography>
        </Box>
      );
    }
  }

  if (exportType === 'tableOfContents') {
    return (
      <Box>
        <Typography
          variant='h6'
          sx={{ mb: 2 }}
        >
          {t('draftManuals.export.tableOfContentsTitle')}
        </Typography>

        <Stack spacing={1}>
          {isFullManual(manual) &&
            manual.prefaces
              ?.filter((preface: any) => preface.state !== 'DISABLED')
              .map((preface: any, index: number) => (
                <Typography
                  key={preface.id || index}
                  variant='body2'
                >
                  {parse(preface.title || '')}
                </Typography>
              ))}

          {isFullManual(manual) &&
            manual.chapters
              ?.filter((chapter: any) => chapter.state !== 'DISABLED')
              .map((chapter: any) => (
                <Box key={chapter.id}>
                  <Typography
                    variant='subtitle2'
                    sx={{ mt: 1 }}
                  >
                    {parse(chapter.title || '')}
                  </Typography>

                  {chapter.policies
                    ?.filter((policy: any) => policy.state !== 'DISABLED')
                    .map((policy: any) => (
                      <Typography
                        key={policy.id}
                        variant='body2'
                        sx={{ pl: 2 }}
                      >
                        {t('draftManuals.export.policyLabel')} {policy.policyNumber}: {parse(policy.title || '')}
                      </Typography>
                    ))}
                </Box>
              ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant='h6'
        sx={{ mb: 2 }}
      >
        {manual?.name || t('draftManuals.export.manual')}
        {t('draftManuals.export.fullExportSuffix')}
      </Typography>

      <Typography
        variant='body2'
        sx={{ mb: 2 }}
      >
        {t('draftManuals.export.exportWillInclude')}
      </Typography>

      <Stack spacing={1}>
        {isFullManual(manual) &&
          manual.prefaces &&
          manual.prefaces.filter((preface: any) => preface.state !== 'DISABLED').length > 0 && (
            <Typography variant='body2'>
              {manual.prefaces.filter((preface: any) => preface.state !== 'DISABLED').length === 1
                ? t('draftManuals.export.prefacesCount', '', {
                    count: manual.prefaces.filter((preface: any) => preface.state !== 'DISABLED').length.toString(),
                  })
                : t('draftManuals.export.prefacesCountPlural', '', {
                    count: manual.prefaces.filter((preface: any) => preface.state !== 'DISABLED').length.toString(),
                  })}
            </Typography>
          )}

        {isFullManual(manual) &&
          manual.chapters &&
          manual.chapters.filter((chapter: any) => chapter.state !== 'DISABLED').length > 0 && (
            <Typography variant='body2'>
              {manual.chapters.filter((chapter: any) => chapter.state !== 'DISABLED').length === 1
                ? t('draftManuals.export.chaptersCount', '', {
                    count: manual.chapters.filter((chapter: any) => chapter.state !== 'DISABLED').length.toString(),
                  })
                : t('draftManuals.export.chaptersCountPlural', '', {
                    count: manual.chapters.filter((chapter: any) => chapter.state !== 'DISABLED').length.toString(),
                  })}
            </Typography>
          )}

        {isFullManual(manual) && manual.chapters && (
          <Typography variant='body2'>
            {t('draftManuals.export.policiesCount', '', {
              count: manual.chapters
                .filter((chapter: any) => chapter.state !== 'DISABLED')
                .reduce(
                  (total: number, chapter: any) =>
                    total + (chapter.policies?.filter((policy: any) => policy.state !== 'DISABLED').length || 0),
                  0
                )
                .toString(),
            })}
          </Typography>
        )}

        {showEditLevelTags && <Typography variant='body2'>{t('draftManuals.export.editLevelTagsIncluded')}</Typography>}

        {showAccreditationTags && (
          <Typography variant='body2'>{t('draftManuals.export.accreditationTagsIncluded')}</Typography>
        )}
      </Stack>
    </Box>
  );
};
